import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { store } from './store';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuth } from './store/slices/authSlice';
import type { RootState } from './store';

// Components
import { MainLayout } from './components/MainLayout';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import Dashboard from './pages/dashboard';
import Terms from './pages/Terms';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import NotFound from './pages/NotFound';
import PublicEventPage from './pages/events/PublicEventPage';
import { PaymentSuccessPage } from './pages/payment/PaymentSuccessPage';

// Dashboard Pages
import EventList from './pages/dashboard/events/EventList';
import EventDetail from './pages/dashboard/events/EventDetail';
import CreateNewEvent from './pages/dashboard/events/CreateNewEvent';
import EditEvent from './pages/dashboard/events/EditEvent';
import ProfileSettings from './pages/dashboard/settings/ProfileSettings';
import BillingSettings from './pages/dashboard/settings/BillingSettings';
import SecuritySettings from './pages/dashboard/settings/SecuritySettings';
import Overview from './pages/dashboard/Overview';
import HowToUse from './pages/dashboard/HowToUse';

// Email verification page
import VerifyEmail from './pages/auth/VerifyEmail';

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// App içeriği - Redux hooks'ları burada kullanılacak
const AppContent = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const token = useSelector((state: RootState) => state.auth.token);

  // Auth check effect
  useEffect(() => {
    console.log('[App] Checking auth on mount');
    dispatch(checkAuth());
  }, [dispatch]);

  // Log authentication state changes
  useEffect(() => {
    console.log('[App] Auth state changed - isAuthenticated:', isAuthenticated, 'token exists:', !!token);
  }, [isAuthenticated, token]);

  return (
    <MainLayout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/e/:url" element={<PublicEventPage />} />
        <Route path="/terms" element={<Terms />} />
        
        {/* Auth Routes - Redirect to dashboard if already authenticated */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        } />
        <Route path="/register" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />
        } />
        <Route path="/forgot-password" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />
        } />
        <Route path="/reset-password" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />
        } />
        
        {/* Protected Routes */}
        <Route path="/dashboard/*" element={
          <ProtectedRoute>
            <Routes>
              <Route index element={<Overview />} />
              <Route path="events" element={<EventList />} />
              <Route path="events/new" element={<CreateNewEvent />} />
              <Route path="events/:url" element={<EventDetail />} />
              <Route path="events/edit/:url" element={<EditEvent />} />
              <Route path="how-to-use" element={<HowToUse />} />
              <Route path="settings/profile" element={<ProfileSettings />} />
              <Route path="settings/billing" element={<BillingSettings />} />
              <Route path="settings/security" element={<SecuritySettings />} />
            </Routes>
          </ProtectedRoute>
        } />
        
        <Route path="/verify-email" element={<VerifyEmail />} />
        
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        } />
        
        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ToastContainer />
    </MainLayout>
  );
};

// Ana App bileşeni
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;